import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { API_URL } from 'data/services/apiConstants';
import { PropertyFavoriteIcon } from '../PropertyFavoriteIcon';
import './PropertyCard.css';

const PropertyCard = ({
	property,
	portfolioId,
	onDeleteClick,
	onClick,
	onNotificationClick,
	onMenuClick,
	showNotificationIcon = true,
	showFavoriteButton = true,
	showDeleteButton = true,
	showMenuButton = true,
	notifications = 0,
}) => {
	const placeholderImage = '/placeholder-building.jpg';
	const resolvedThumbnailSrc = useMemo(() => {
		if (!property.thumbnail_url) {
			return placeholderImage;
		}

		if (
			property.thumbnail_url.startsWith('http://') ||
			property.thumbnail_url.startsWith('https://') ||
			property.thumbnail_url.startsWith('//') ||
			property.thumbnail_url.startsWith('data:')
		) {
			return property.thumbnail_url;
		}

		return `${API_URL}${property.thumbnail_url}`;
	}, [property.thumbnail_url, placeholderImage]);

	const [imageSrc, setImageSrc] = useState(resolvedThumbnailSrc);

	useEffect(() => {
		setImageSrc(resolvedThumbnailSrc);
	}, [resolvedThumbnailSrc]);

	const handleCardClick = (e) => {
		// Prevent navigation if clicking on action icons
		if (
			e.target.closest('.notification-icon') ||
			e.target.closest('.property-favorite-icon') ||
			e.target.closest('.delete-icon') ||
			e.target.closest('.property-card-action')
		) {
			return;
		}

		if (onClick) {
			onClick(property);
		}
	};

	const handleDeleteClick = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (onDeleteClick) {
			onDeleteClick(property);
		}
	};

	const handleNotificationClick = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (onNotificationClick) {
			onNotificationClick(property);
		}
	};

	const handleMenuClick = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (onMenuClick) {
			onMenuClick(property);
		}
	};

	// Format the complete address with block/lot and BIN
	const formatAddress = () => {
		const baseAddress = property.address;
		const blockLot =
			property.block && property.lot
				? `[${property.block}/${property.lot}]`
				: '';
		const bin = property.bin ? `[${property.bin}]` : '';

		return `${baseAddress} ${blockLot} ${bin}`.trim();
	};

	return (
		<div className="property-card" onClick={handleCardClick}>
			{/* Top Header */}
			<div className="property-card-header">
				<div className="property-address">{formatAddress()}</div>
				<div className="property-card-actions">
					{/* Notification Icon */}
					{showNotificationIcon && (
						<div
							className="notification-icon"
							onClick={handleNotificationClick}
							title="Notifications"
						>
							<i className="bi bi-bell-fill"></i>
							{notifications > 0 && (
								<span className="notification-badge">{notifications}</span>
							)}
						</div>
					)}

					{/* Favorite Star */}
					{showFavoriteButton && (
						<PropertyFavoriteIcon
							key={property.id}
							property={property}
							isFavorite={property.is_favorite}
							size="medium"
							className="in-card-header"
							showTooltip={true}
						/>
					)}

					{/* Delete Trash */}
					{showDeleteButton && onDeleteClick && (
						<div
							className="delete-icon"
							onClick={handleDeleteClick}
							title="Remove property"
						>
							<i className="bi bi-trash"></i>
						</div>
					)}
				</div>
			</div>

			{/* Property Thumbnail */}
			<div className="property-thumbnail">
				<img
					src={imageSrc}
					alt={property.address}
					className="property-image"
					onError={() => {
						setImageSrc(placeholderImage);
					}}
				/>
			</div>

			{/* Bottom Right Action Menu */}
			{showMenuButton && (
				<div className="property-card-menu">
					<button
						type="button"
						className="property-card-action menu-btn"
						onClick={handleMenuClick}
						title="More options"
					>
						<i className="bi bi-three-dots-vertical"></i>
					</button>
				</div>
			)}
		</div>
	);
};

PropertyCard.propTypes = {
	property: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		address: PropTypes.string.isRequired,
		bin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		block: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		lot: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		bbl: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		borough: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		openCertificates: PropTypes.number,
		openViolations: PropTypes.number,
		openInfrastructure: PropTypes.number,
		last_visited_at: PropTypes.string,
		is_favorite: PropTypes.bool,
		building_classification: PropTypes.string,
		landmark_status: PropTypes.string,
		thumbnail_url: PropTypes.string,
	}).isRequired,
	portfolioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	notifications: PropTypes.number,
	onDeleteClick: PropTypes.func,
	onClick: PropTypes.func,
	onNotificationClick: PropTypes.func,
	onMenuClick: PropTypes.func,
	showNotificationIcon: PropTypes.bool,
	showFavoriteButton: PropTypes.bool,
	showDeleteButton: PropTypes.bool,
	showMenuButton: PropTypes.bool,
};

export default PropertyCard;
